import { useSelector } from 'react-redux';

import { FieldLabel } from '@/components/FieldLabel';
import { Input } from '@/components/Input';
import {
    IEmploymentByIndex,
    selectEmploymentStatusByIndex,
    setEmploymentStatusByIndex,
} from '@/context/borrower';
import { RootState } from '@/context/store';
import { useAppDispatch } from '@/context/storeHooks';
import { getNumber } from '@/utils/valueFormat';

export interface ICasualLength {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'CasualLength';

export function CasualLength({
    baseId,
    showErrors,
}: ICasualLength): JSX.Element {
    // ***** Redux *****
    const employment = useSelector((state: RootState) =>
        selectEmploymentStatusByIndex(state, 0)
    );
    const employmentYears = employment?.years;
    const employmentMonths = employment?.months;
    const dispatch = useAppDispatch();

    // ***** Event Handlers *****
    function handleYearChange(value: string | boolean): void {
        // MAXIMUM
        const MAXIMUM = 99;
        const intValue = getNumber(value, MAXIMUM);
        if (intValue === employmentYears) return;

        const dispatchObjext: IEmploymentByIndex = {
            index: 0,
            employment: {
                ...employment,
                years: intValue,
            },
        };
        dispatch(setEmploymentStatusByIndex(dispatchObjext));
    }
    function handleMonthChange(value: string | boolean): void {
        // MAXIMUM
        const MAXIMUM = 12;
        const intValue = getNumber(value, MAXIMUM);
        if (intValue === employmentMonths) return;

        const dispatchObjext: IEmploymentByIndex = {
            index: 0,
            employment: {
                ...employment,
                months: intValue,
            },
        };
        dispatch(setEmploymentStatusByIndex(dispatchObjext));
    }

    /*
     * One answer covers both fields, so neither is marked on its own.
     * `!employmentMonths` was true for a real 0, which lit the months field
     * red for someone who had entered ten years and nothing else — and 0 is
     * the right answer there. Matches IncomeButtons, which lets the pair
     * through once either half is given.
     */
    const lengthMissing =
        employmentYears === undefined && employmentMonths === undefined;

    // ***** Render *****
    return (
        <div data-testid={testId}>
            {/* One question over both fields. Labelling them "Years" and
                "Months" separately read as two questions and left the thing
                actually being asked unstated. */}
            <FieldLabel
                title='How long have you worked for this employer?'
                as='p'
                required
            />
            <div className='_flex space-x-5'>
                <Input
                    placeholder='2'
                    unit='years'
                    ariaLabel='Years at this employer'
                    showErrors={showErrors && lengthMissing}
                    onChange={handleYearChange}
                    id={`${baseId}-input-casualLength-years`}
                    value={employmentYears?.toLocaleString() ?? ''}
                />
                <Input
                    placeholder='6'
                    unit='months'
                    ariaLabel='Months at this employer'
                    showErrors={showErrors && lengthMissing}
                    onChange={handleMonthChange}
                    id={`${baseId}-input-casualLength-months`}
                    value={employmentMonths?.toLocaleString() ?? ''}
                />
            </div>
        </div>
    );
}
